import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { FormTransformers } from '@/common/utils/formTransformers'
import { CompanyData } from '@/features/companies/types/companies.type'
import { useUpdateUserAccess } from '@/features/privateCompanies/hooks/politics/useUpdateUserAccess'
import { allowedDriverEquipmentBranchHierarchyForConsumptionOptions } from '@/features/privateCompanies/pages/Politics/Show/constants/allowedDriverEquipmentBranchHierarchyForConsumption.constant'
import { branchHierarchyForUserAccessOptions } from '@/features/privateCompanies/pages/Politics/Show/constants/branchHierarchyForUserAccess.constant'
import { driverLicenseRequirementsForConsumptionsOptions } from '@/features/privateCompanies/pages/Politics/Show/constants/driverLicenseRequirementsForConsumptions.constant'
import {
  UpdateUserAccessSchema,
  updateUserAccessSchema,
} from '@/features/privateCompanies/schemas/politics/updateUserAccess.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface UpdateUserAccessModalProps {
  company?: CompanyData
}

export const UpdateUserAccessModal = ({ company }: UpdateUserAccessModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<UpdateUserAccessSchema>({
    resolver: zodResolver(updateUserAccessSchema),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['company', company?.id] })
    setIsOpen(false)
  }

  const { mutate: updateUserAccess, isPending: updateUserAccessIsPending } = useUpdateUserAccess({
    companyId: company?.id,
    onSuccess,
    onError: FormTransformers.errorsTransformer<UpdateUserAccessSchema>({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<UpdateUserAccessSchema> = (data) => {
    updateUserAccess(data)
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        company: {
          allowed_driver_equipment_branch_hierarchy_for_consumption:
            allowedDriverEquipmentBranchHierarchyForConsumptionOptions.find(
              ({ value }) => value === company?.allowed_driver_equipment_branch_hierarchy_for_consumption,
            ),
          branch_hierarchy_for_user_access: branchHierarchyForUserAccessOptions.find(
            ({ value }) => value === company?.branch_hierarchy_for_user_access,
          ),
          driver_license_requirements_for_consumptions: driverLicenseRequirementsForConsumptionsOptions.find(
            ({ value }) => value === company?.driver_license_requirements_for_consumptions,
          ),
        },
      })
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_user_access_modal"
          operationKlassToCheckPermission="Admin::Client::Companies::UpdateUsersAccessConfigurations"
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando acessos de usuários"
      dataTestid="update_user_access"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateUserAccessIsPending}
      confirmButtonIsLoading={updateUserAccessIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="company.allowed_driver_equipment_branch_hierarchy_for_consumption"
              label="Equipamentos que o usuário pode transacionar"
              errorMessage={errors?.company?.allowed_driver_equipment_branch_hierarchy_for_consumption?.message}
              options={allowedDriverEquipmentBranchHierarchyForConsumptionOptions}
              isDisabled={updateUserAccessIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.branch_hierarchy_for_user_access"
              label="Filiais que o usuário pode acessar os recursos"
              errorMessage={errors?.company?.branch_hierarchy_for_user_access?.message}
              options={branchHierarchyForUserAccessOptions}
              isDisabled={updateUserAccessIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="company.driver_license_requirements_for_consumptions"
              label="Validar CNH"
              errorMessage={errors?.company?.driver_license_requirements_for_consumptions?.message}
              options={driverLicenseRequirementsForConsumptionsOptions}
              isDisabled={updateUserAccessIsPending}
              markAsRequired
            />
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
